// Local imports
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link as ExternalLink, List, ListItem, ListItemText, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Api } from '../../services/api';
import { Recipe } from '../../services/api/models';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import idx from 'idx';

// Component definition
function ViewRecipePage() {
  const {recipeId} = useParams();
  const [recipe, setRecipe] = useState<Recipe>();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => { // Made this function async
    setOpen(false);

    if (!recipeId) {
      return;
    }

    await Api.Recipe.delete(recipeId)
      .then(() => {
        navigate('/search');
      }).catch(error => {
        console.warn(error);
      });
  };


  useEffect(() => {
    // call api or anything
    async function fetchRecipe(recipeId: string) {
      Api.Recipe.get(recipeId)
        .then(response => {
          setRecipe(response.data.recipe)
        }).catch(error => {
          console.warn(error);
        });
    }

    if (recipeId) {
      fetchRecipe(recipeId)
    }
 }, [recipeId]);

  if (recipe) {
    return (
      <Box position="relative">
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
        <DialogTitle id="alert-dialog-title">
          {'Confirm Recipe Deletion'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to permanently delete <b>{recipe?.name}</b>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button startIcon={<DeleteIcon />} onClick={handleConfirm} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        <Box sx={{margin: 2, position: 'absolute', top: 8, right: 8, zIndex: 1}}>
          <Link to={`/recipe/${recipe.id}/edit`}>
            <Button type='submit' variant='contained' startIcon={<EditIcon />}>Edit</Button>
          </Link>
          <Button type='button' variant='contained' startIcon={<DeleteIcon />} sx={{ marginLeft: 2}}  onClick={handleClickOpen}>Delete</Button>
        </Box>
        <Card>
          { recipe.images.length > 0 &&
          <CardMedia
            sx={{ height: 250 }}
            image={'' + idx(recipe, _ => _.images?.[0].url)}
            title="green iguana"
          />
          }
          <CardContent>
          <Typography variant='h1'>{recipe?.name}</Typography>
          <Typography paragraph>
            {recipe?.description}
          </Typography>
          <Card>
            <Typography>Category: </Typography><Typography>{recipe?.category?.name}</Typography>
          </Card>
          <Card>
            <Typography>Cuisine: </Typography><Typography>{recipe?.cuisine?.name}</Typography>
          </Card>
          <Card>
            { recipe.source_url &&
              <Typography>Source:&nbsp;
                <ExternalLink href={recipe?.source_url} target="_blank" rel="noopener,noreferrer">
                 {recipe?.source_url} <LaunchIcon fontSize='small'/>
                </ExternalLink>
              </Typography>
            }
          </Card>
          <Stack direction="row" spacing={2}>
              <Card>
                <Typography variant="h5"><AccessAlarmIcon sx={{fontSize: 20}}/> Prep Time: {recipe.prep_time}</Typography>
              </Card>
              <Card>
                <Typography variant="h5"><AccessAlarmIcon sx={{fontSize: 20}}/> Cook Time: {recipe.cook_time}</Typography>
              </Card>
              <Card>
                <Typography variant="h5"><AccessAlarmIcon sx={{fontSize: 20}}/> Total Time: {recipe.total_time}</Typography>
              </Card>
          </Stack>
          <Typography variant="h2">Ingredients</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="ingredients table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Amount</TableCell>
                  <TableCell align="left">Unit</TableCell>
                  <TableCell align="left">Ingredient</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipe?.ingredients?.map((ingredient, i) => (
                  <TableRow
                    key={i}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="left">
                      {ingredient.amount}
                    </TableCell>
                    <TableCell align="left">
                      {ingredient.unit}
                    </TableCell>
                    <TableCell align="left">
                      {ingredient.name}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h2">Instructions</Typography>
          <List>
            {recipe?.steps?.map((step, index) => (
              <ListItem key={index} alignItems="flex-start">
                <ListItemText
                  primary={
                    <>
                    {step.name &&
                      <Typography variant="h3">{step.name}</Typography>
                    }
                    {!step.name &&
                      <Typography variant="h3">Step {index + 1}</Typography>
                    }
                    <Typography>{step.text}</Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Card>
            <Stack direction="row" spacing={2}>
                {recipe?.tags?.map((tag, i) => (
                  <Card key={i}>
                    <Typography variant="h5"><LocalOfferOutlinedIcon sx={{fontSize: 15}}/> {tag.name}</Typography>
                  </Card>
                ))}
            </Stack>
          </Card>
          </CardContent>
        </Card>
      </Box>
    );
  } else {
    return (
      <Box>
        <Typography variant='h1'>Recipe Not Found</Typography>
      </Box>
    )
  }
}


// Default export
export default ViewRecipePage;
